import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import './CreateUser.css';
import '../SettingsTabs/Settings.css';

import { useNavigate } from "react-router-dom";
import pwsLogo from "../../../assets/logo800.webp";
import ImageWithSkeleton from "../../../components/ui/ImageWithSkeleton";
import { getStrengthLevel, isPasswordStrongEnough } from "../../../utils/passwordStrength";
import { COUNTRY_CODES } from "../../../utils/countryCodes";

export default function CreateUser() {
  const { t } = useTranslation('createUser');
  const { t: tc } = useTranslation('common');
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Not locked to Norway - defaults there since most signups are local, but
  // the picker below lets anyone select their own country. On submit this is
  // glued onto `phone` as a full MSISDN, same convention MyCart already uses
  // for checkout's phone_number.
  const [countryCode, setCountryCode] = useState("47");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("");
  const [createdEmail, setCreatedEmail] = useState(null);
  const navigate = useNavigate();
  const strength = getStrengthLevel(password, tc);

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (!isPasswordStrongEnough(password)) {
      setMsg(t('passwordTooWeak'));
      setMsgType("error");
      return;
    }

    if (!acceptedTerms) {
      setMsg(t('mustAcceptTerms'));
      setMsgType("error");
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();
      const fullPhone = phone.trim() ? countryCode + phone.trim() : "";

      // The backend creates the account itself (with this real password) in the
      // same call that sends our branded confirmation email - calling
      // supabase.auth.signUp() here too would create the account a second way
      // and trigger Supabase's own separate confirmation email for the same
      // signup (see authLinks.js's /email-verification).
      try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/email-verification`, {
          email: normalizedEmail,
          password,
          continueUrl: window.location.origin + '/login',
          profile: { firstName, lastName, phone: fullPhone },
        });
        setMsg(t('accountCreatedCheckEmail'));
        setMsgType("success");
        setCreatedEmail(normalizedEmail);
      } catch (emailError) {
        // Refused before the account was created (e.g. phone already taken) -
        // keep the form so the customer can fix it.
        if (emailError.response?.status === 409) {
          setMsg(emailError.response.data.error);
          setMsgType("error");
          setLoading(false);
          return;
        }
        setMsg(t('accountCreatedEmailFailed', { message: emailError.response?.data?.error || emailError.message }));
        setMsgType("error");
        setCreatedEmail(normalizedEmail);
      }

      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setPassword("");
    } catch (error) {
      setMsg(t('errorWithMessage', { message: error.message }));
      setMsgType("error");
    }

    setLoading(false);
  }

  const resendVerificationEmail = async () => {
    if (!createdEmail) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/email-verification`, {
        email: createdEmail,
        continueUrl: window.location.origin + '/login',
      });
      setMsg(t('verificationResent'));
      setMsgType("success");
    } catch (error) {
      setMsg(t('errorSendingEmail', { message: error.response?.data?.error || error.message }));
      setMsgType("error");
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-content">
        <div className="login-card">
          <form className="login-form-new" onSubmit={handleSubmit}>
            <div className="login-form-header">
              <div className="login-logo-wrap">
                <ImageWithSkeleton
                  src={pwsLogo}
                  alt="PLAYWORLD"
                  loading="eager"
                  imgStyle={{ width: "100%", height: "auto", display: "block" }}
                  style={{ width: "100%", zIndex: 2, position: "relative" }}
                />
              </div>
              <div className="login-card-title">{t('createAccount')}</div>
            </div>

            {msg && (
              <div className={msgType === "success" ? "login-message-success" : "login-error-message"}>
                {msg}
              </div>
            )}

            <div className="login-field">
              <label>{t('firstName')}</label>
              <input placeholder={t('firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>

            <div className="login-field">
              <label>{t('lastName')}</label>
              <input placeholder={t('lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>

            <div className="login-field">
              <label>{t('email')}</label>
              <input type="email" placeholder={t('emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="login-field">
              <label>{t('phone')}</label>
              <div className="cu-phone-row">
                <div className="cu-phone-prefix-wrap">
                  {/* Same overlay trick as MyCart's CARD picker: a visible
                      compact flag+code span behind an invisible-text <select>,
                      so the closed box stays compact while the native
                      dropdown still shows the full flag+name+code per option. */}
                  <span className="cu-phone-prefix-display" aria-hidden="true">
                    {(COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0]).flag} +{countryCode}
                  </span>
                  <select
                    className="cu-phone-prefix-select"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    aria-label={t('countryCodeLabel')}
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.name} +{c.code}</option>
                    ))}
                  </select>
                </div>
                <input
                  type="tel"
                  placeholder={t('phonePlaceholderLocal')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 12))}
                />
              </div>
            </div>

            <div className="login-field">
              <label>{t('password')}</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
              <div className="strength-bar"><div className="strength-fill" style={{ width: strength.width, background: strength.color }} /></div>
              <div className="strength-label" style={{ color: password ? strength.color : '#5B6A99' }}>
                {password ? strength.label : t('passwordHint')}
              </div>
            </div>

            <label className="terms-checkbox-row">
              <span className="terms-checkbox-input-wrap">
                <input
                  type="checkbox"
                  className="terms-checkbox-input"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                />
              </span>
              <span>
                {t('iAcceptPrefix')}{' '}
                <button type="button" className="terms-checkbox-link" onClick={() => navigate('/personvern')}>
                  {t('privacyAndTermsLink')}
                </button>.
              </span>
            </label>

            <button className="login-btn-submit" type="submit" disabled={loading}>
              {loading ? t('saving') : t('createAccountButton')}
            </button>

            {createdEmail && (
              <button type="button" className="login-btn-secondary" onClick={resendVerificationEmail}>
                {t('resendVerificationLink')}
              </button>
            )}

            <div className="login-divider-row">
              <div className="login-divider-line" />
              <span style={{ fontSize: 12, color: '#5B6A99', whiteSpace: 'nowrap' }}>{t('hasAccount')}</span>
              <div className="login-divider-line" />
            </div>

            <div className="login-signup-row">
              <button type="button" className="login-signup-link" onClick={() => navigate('/login')}>
                {t('logIn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
